﻿/*
   Copied from web-pli: https://github.com/pandell/web-pli/blob/90be145156d256c6d34e625393e9f739d4f73077/util/serverComputed.ts
   Adds functionality to inform the ServerCalculationContributor of call failure which won't be added to the web-pli version:
   https://github.com/pandell/web-pli/issues/208
*/

import { PliCallResponse } from "pli/core/call";

/// <reference path="../../typings/app.d.ts" />

/* tslint:disable:no-var-requires no-require-imports */
const pli = require("pli/core");

import * as ko from "pli/knockout";
/* tslint:enable:no-var-requires no-require-imports */


export interface ContributorState {
    isReady: ko.Observable<boolean>;
}


/**
 * Interface that should be implemented by every part of target viewmodel that requires server calculation.
 *
 * For more info, see https://github.com/pandell/web-pli/wiki/Server-Calculations
 */
export interface ServerCalculationContributor {

    /**
     * Apply result of server calculation to this contributor. Should be defensive (don't assume anything).
     *
     * Use "context.currentData" to access result data applicable to this contributor.
     * Use "context.visit" to apply results to child contributors.
     */
    applyServerCalculationResult(context: ServerCalculationContext): void;

    /**
     * Build data for server calculation (anything goes, "null" indicates "this contributor is currently not interested in server calculation").
     *
     * Use "context.visit" to gather inputs from child contributors
     * (however this contributor is responsible for packaging those
     * gathered inputs in its own return value).
     *
     * Optimization note: if the root contributor is not interested in server
     * calculation, calculation engine will not make a server call; root
     * contributor can use null return values from its child contributors
     * to determine whether it is interested in calculation; child contributors
     * can use null return values from grand-children; and so on (recursively).
     */
    buildServerCalculationData(context: ServerCalculationContext): any;

    /**
     * Optional state of the contributor. The server call is not made if state.isReady is false.
     */
    state?: ContributorState;

}


/**
 * Information and operations relevant to the current position in the viewmodel when gathering/building calculation input or applying calculation result.
 *
 * Should only be created by "ServerCalculation", will be passed to
 * "applyServerCalculationResult" and "buidServerCalculationData" as a parameter.
 *
 * For more info, see https://github.com/pandell/web-pli/wiki/Server-Calculations
 */
export class ServerCalculationContext {

    /**
     * Create a new instance of server calculation context.
     *
     * Every time we traverse a viewmodel (either to gather inputs or apply result),
     * new context instance will be created, but the same context instance is used
     * to visit all contributors during viewmodel traversal.
     *
     * @param {string} methodName
     *     Either "applyServerCalculationResult" or "buildServerCalculationData".
     * @param {any = null} rootData
     *     Result data to apply to root contributor (and its children via recursive "visit" calls).
     *     Only specified when "methodName==='applyServerCalculationResult'".
     */
    constructor(methodName: string, rootData: any = null) {
        this.rootData = this.currentData = rootData;
        this._methodName = methodName;
    }

    /**
     * Visit a new contributor (usually a child of the current contributor).
     *
     * @param {any} contributor
     *     Reference to the contributor to visit (typically child property of current contributor).
     * @param {string} dataKey
     *     Key to use to get from "this.currentData" (result data applicable to the current contributor) to result data applicable to "contributor".
     *     Only required when calling "visit" in "applyServerCalculationResult" methods.
     *
     * @returns {any}
     *     Result of invoking the "_methodName" on the specified contributor.
     */
    visit(contributor: any, dataKey?: string): any {
        let methodToCall: Function | string;
        let result: any = null;
        const originalData = this.currentData;
        const currentData = (typeof this.currentData === "object" && this.currentData !== null && typeof dataKey === "string"
            ? this.currentData[dataKey]
            : this.currentData);

        if (ko.isObservable(contributor) && typeof (contributor as any).indexOf === "function") { // naive observable array detection
            const originalCollectionName = this.currentCollectionName;
            this.currentCollectionName = dataKey;
            methodToCall = (contributor as any)[this._methodName];
            if (typeof methodToCall === "function") {
                methodToCall.call(contributor, this);
            }

            this.currentData = currentData;
            let a = contributor(), b: any[] = [], foundResult = false, itemResult: any;
            for (let i = 0, l = a.length; i < l; i += 1) {
                itemResult = this.visit(a[i], i.toString());
                if (itemResult === undefined) { itemResult = null; }
                if (itemResult !== null) { foundResult = true; }
                b.push(itemResult);
            }

            this.currentCollectionName = originalCollectionName;
            if (foundResult) {
                result = b;
            }
        } else if (typeof contributor === "object") {
            this.currentData = currentData;
            methodToCall = contributor[this._methodName];
            if (typeof methodToCall === "function") {
                result = methodToCall.call(contributor, this);
                if (result === undefined) { result = null; }
            }
        }

        this.currentData = originalData;
        return result;
    }

    /**
     * Gets name of the collection (observable array) that we last visited.
     *
     * In other words, what collection is the current contributor member of.
     * When null, we are still visiting the root contributor.
     */
    currentCollectionName: string = null; // changed in "visit"

    /**
     * Gets the result data with which is the current contributor being visited.
     *
     * Will be not-null only when "rootData" is specified, and "rootData"
     * is only specified when applying calculation result (i.e. "currentData"
     * property should not be used when building calculation inputs).
     */
    currentData: any; // set in "constructor" (to "rootData"), changed in "visit"

    /**
     * Gets the root result data (as received by "applyResult").
     *
     * Will be not-null only when applying calculation result
     * (i.e. not when building calculation inputs).
     */
    rootData: any; // set in "constructor", readonly


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /** Name of the method we will be "visit"-ing in target contributors */
    private _methodName: string; // set in "constructor", readonly

}


/**
 * Move complicated or precision-sensitive calculations to the server.
 *
 * For more info, see https://github.com/pandell/web-pli/wiki/Server-Calculations
 */
export class ServerCalculation {

    /**
     * Creates and starts a new instance of server calculation engine.
     * If falsy "calculationPliCallName" or "target" is specified,
     * the created calculation engine instance will be inactive.
     *
     * @param {string | KnockoutObservable<string>} calculationPliCallName
     *     Name of the Pli call that will be invoked every time a change is detected.
     * @param {ServerCalculationContributor} target
     *     Root of the viewmodel for which we are performing calculations.
     * @param {number} throttleEvaluationMilliseconds
     *     Bundle all changes that happen within this period into a single server call.
     *     When negative value or 0 is specified, no throttling is used.
     *     @default 250 milliseconds
     * @param {boolean = true} verbose
     *     Write diagnostic information using "console.log" (true) or not (false, default).
     *     @default false
     */
    constructor(
        calculationPliCallName: string | ko.Observable<string>,
        target: ServerCalculationContributor,
        throttleEvaluationMilliseconds: number = 250,
        verbose: boolean = false) {
        this._call = null;
        this._callName = null;
        this._target = null;
        this._verbose = verbose;
        this._watcher = null;

        const callName = (ko.unwrap(calculationPliCallName) || "").toString();
        if (callName && target) {
            this._callName = callName;
            this._target = target;
            this._watcher = ko.computed({
                read: this._beginCalculation,
                owner: this,
                deferEvaluation: true,
                pure: false // keep updating even when it has no subscribers
            });

            if (throttleEvaluationMilliseconds > 0) {
                this._watcher.extend({ rateLimit: throttleEvaluationMilliseconds }); // see http://knockoutjs.com/documentation/rateLimit-observable.html
            }

            this._watcher(); // manually trigger first evaluation (that is expected to build a list of observable dependencies)
        }
    }

    /**
     * Apply specified calculation result to the viewmodel for which we are performing calculations.
     *
     * Exposed publically so that it can be called manually, e.g. during viewmodel load
     * (this is in addition to the automatic calls invoked internally by
     * this calculation engine).
     *
     * @param {any} result
     *     Result of server calculation to apply to root contributor (and its children via recursive "visit" calls).
     *     Will be available as "context.rootData" in "applyServerCalculationResult" methods).
     */
    applyResult(result: any): void {
        new ServerCalculationContext("applyServerCalculationResult", result).visit(this._target);
    }

    /**
     * Gets an observable flag that indicates whether server calculation is currently talking to Pli server (true) or is idle (false).
     *
     * Can be used to update UI to indicate communication with the server
     * (e.g. make some fields readonly or show a spinner).
     *
     * Should be treated as a readonly property.
     */
    calculating: ko.Observable<boolean> = ko.observable(false);


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /**
     * Traverse target viewmodel, extracting data for server calculation and setting up observable dependencies
     *
     * The dependencies are used to re-trigger calculation in the future
     * if any calculation input changes.
     */
    private _beginCalculation(): void {
        const state = (this._target && this._target.state);

        if (!state || ko.unwrap(state.isReady)) { // create dependency on state.isReady + run calculation only if target is ready
            const calculationData = new ServerCalculationContext("buildServerCalculationData").visit(this._target); // create dependency on all observables used in calculation request
            if (calculationData) {
                const callPromise = pli.call({
                    name: this._callName,
                    data: calculationData,
                    owner: this
                });

                this._call = callPromise.xhr;
                this.calculating(true); // notify subscribers that we are calculating

                callPromise.always(ServerCalculation._endCalculation);
            }
        }
    }

    /**
     * Process result of server calculation. Calls "applyResult" unless there were problems.
     */
    private static _endCalculation(response: PliCallResponse<any, ServerCalculation>): void {
        if (response.owner._call !== response.clientXhr) {
            if (response.owner._verbose) { console.warn("Ignoring server calculation response", response); }
        } else {
            response.owner.applyResult(response);
            response.owner._call = null;
            response.owner.calculating(false); // notify subscribers that we are done calculating
        }
    }

    /** Current Pli call performing calculation on the server or null. */
    private _call: JQueryXHR; // set in constructor, changed in "_beginCalculation" and "_endCalculation"

    /** Name of the Pli call that will be invoked every time a change is detected. */
    private _callName: string; // set in "constructor", readonly

    /** Root of the viewmodel for which we are performing calculations. */
    private _target: ServerCalculationContributor; // set in "constructor", readonly

    /** Write diagnostic information using "console.log" */
    private _verbose: boolean; // set in "constructor", readonly

    /** Observable that detects changes in viewmodel (which will trigger server calculation call). */
    private _watcher: ko.Computed<any>; // set in "constructor", readonly

}



// WEBPACK FOOTER //
// ./app/common-server-computed-ts/serverComputed.ts